<template>
  <div class="modal-footer">
    <div class="be-care-for">
      <img
        width="338"
        height="30"
        class="icon"
        src="@/assets/img/PatientFile/be-care-fo.png"
        alt="be-care-fo"
      />
    </div>
    <template v-if="doctorInfo?.ucDoctorId">
      <a-button v-if="isAdmin" size="large" @click="handleRevisit(2)">分配回访池</a-button>
      <a-button size="large" @click="handleRevisit(1)">
        加入回访池
        <a-tooltip
          v-if="nextFollowupTime.hasNextFollowupDate"
          :overlayStyle="{ minWidth: '350px' }"
          overlayClassName="patient-cdr-tooltip"
          placement="top"
          :title="`下次回访时间：${moment(nextFollowupTime.nextFollowupAt).format(
            'YYYY-MM-DD'
          )}  健管-${nextFollowupTime.nextFollowupDaName}`"
        >
          <QuestionCircleFilled class="icon" :style="{ color: '#ccc', marginLeft: '6px' }" />
        </a-tooltip>
      </a-button>
    </template>
    <!-- 没有医生无法添加 -->
    <a-button
      :disabled="!doctorInfo.doctorId || patientType === 0"
      size="large"
      @click="openMedicalCase"
    >
      添加预病历
    </a-button>
    <!-- 未完成注册登录按钮，发送短信、拨打电话可支持，
    发起私聊、查看IM会话，置灰无法点击 -->
    <a-button size="large" :disabled="patientType === 0" @click="chatRecord">聊天记录</a-button>
    <a-button size="large" @click="call">拨打电话</a-button>
    <a-button size="large" @click="onSendSMS" :disabled="!doctorInfo?.ucDoctorId">
      发送短信
    </a-button>
    <a-button
      size="large"
      :disabled="patientType === 0"
      @click="handlePrivateChat(0)"
      type="primary"
    >
      发起私聊
    </a-button>
    <SendSMS ref="sms"></SendSMS>
    <a-modal
      :visible="chatRecordVisible"
      :footer="null"
      title="聊天记录"
      :width="1000"
      centered
      @cancel="handleCancel"
    >
      <HistoryRecord
        :ucPatientId="info?.ucPatientId"
        :ucDoctorId="doctorInfo?.ucDoctorId"
        :groupId="disease.doctorPatientGroupId"
        :guideConsultGroupId="disease.guideGroupId"
        :privateId="disease.patientGroupId"
        :doctorDaGroupId="disease.doctorGroupId"
      />
    </a-modal>
    <PreMedicalRecordModal ref="PreMedicalRecordModalRef" />
    <!-- 需求调整：通话结束不再自动弹出「主动服务小结」弹窗，先注释保留 -->
    <!-- <ProactiveService v-if="!workOrderId" :accountType="1"></ProactiveService> -->
    <RevisitConfirm ref="revisitConfirmRef" :userId="userId" />
  </div>
</template>

<script setup>
import PreMedicalRecordModal from '@/components/PreMedicalRecordModal'
import { computed, createVNode, ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { Modal } from 'ant-design-vue'
import { ExclamationCircleOutlined, QuestionCircleFilled } from '@ant-design/icons-vue'
import { callOut } from '@/utils/Tccc'
import useRevisitPoolGuard from '@/hooks/useRevisitPoolGuard'
import SendSMS from '@/components/SendSMS/index.vue'
import HistoryRecord from '@/views/WorkPlace/Container/chat/history.vue'
import { medicalGuide, patientChatForArchives } from '@/api/patientFile'
import useOrder from '@/views/WorkPlace/components/WorkOrder/hooks/useOrder.js'
// 需求调整：通话结束不再自动弹出「主动服务小结」弹窗，先注释保留
// import ProactiveService from '@/components/ProactiveService/index.vue'
import { updateMatters } from '@/api/HealthRecord'
import RevisitConfirm from '@/views/UserPool/components/RevisitConfirm.vue'
import moment from 'moment'
const store = useStore()
const router = useRouter()
const { changeToProcess } = useOrder()
const workOrderId = computed(() => store.state.patientFile.workOrderId) // 工单ID
const patientType = computed(() => store.state.patientFile.patientType) // 患者类型- 0:预建档患者，1:普通患者
const doctorInfo = computed(() => store.state.patientFile.bindUser.doctorInfo) // 当前查询的绑定信息
const nextFollowupTime = computed(() => store.state.patientFile.nextFollowupTime) // 下次回访时间
const info = computed(() => store.state.patientFile.info) // 患者 和 医生信息
const disease = computed(() => store.state.patientFile.disease)
const chatRecordVisible = ref(false)
const sms = ref()
const isAdmin = computed(() => store.state.user.isAdmin)
const userId = computed(() => store.state.user.userId)

// 拨打电话
const call = () => {
  const doCall = crossPoolParams => {
    const params = {
      calleeType: patientType.value === 0 ? -1 : 1,
      calleeUcUserId: patientType.value === 0 ? info.value.prePatientId : info.value.ucPatientId,
      doctorId: doctorInfo.value.doctorId,
      // 本次通话对应的医生用户中心 id，供 tryJoinActiveFollowup 使用
      ucDoctorId: doctorInfo.value.ucDoctorId,
      workOrderIds: workOrderId.value ? [workOrderId.value] : [],
      businessType: workOrderId.value ? 1 : 0,
      // 跨回访池确认拨打埋点参数，随拨打参数透传至 record-save-before-call
      ...crossPoolParams
    }
    callOut(params, {
      userType: '患者',
      name: info.value.name
    }).then(result => {
      if (result.cdrId) {
        const params = {
          cdrId: result.cdrId,
          serviceMatters: '',
          mattersRemark: ''
        }
        updateMatters(params)
      }
    })
  }
  const confirmCall = () => {
    Modal.confirm({
      centered: true,
      title: '确定拨打电话',
      icon: createVNode(ExclamationCircleOutlined),
      content: `确定给患者-${info.value.name}拨打电话吗？`,
      okText: '拨打',
      cancelText: '取消',
      onOk: () => doCall()
    })
  }
  // 拨打患者前校验是否已在回访任务池中（非结束态）
  useRevisitPoolGuard({
    ucPatientId: patientType.value === 0 ? info.value.prePatientId : info.value.ucPatientId,
    // 患者档案下方为一对一医患关系，已确定医生，直接带上 ucDoctorId
    ucDoctorId: doctorInfo.value.ucDoctorId,
    source: 'patientFile',
    onPass: confirmCall,
    onContinue: doCall
  })
}

// 发送短信
const onSendSMS = () => {
  const role = 1
  const patientInfo = {
    id: info.value.ucPatientId,
    name: info.value.name
  }
  const dInfo = {
    id: doctorInfo.value.ucDoctorId,
    name: doctorInfo.value.doctorName
  }
  const wid = workOrderId.value ? workOrderId.value : null
  sms.value.open(role, patientInfo, dInfo, wid)
}

// 聊天记录
const chatRecord = () => {
  chatRecordVisible.value = true
}

// 关闭聊天记录
const handleCancel = () => {
  chatRecordVisible.value = false
}

// 发起私聊  createFlag先传0，如果有弹窗：是否继续创建，点击确认再传1
const handlePrivateChat = async createFlag => {
  try {
    const params = {
      patientId: info.value.patientId,
      ucPatientId: info.value.ucPatientId,
      ucDoctorId: doctorInfo.value.ucDoctorId,
      createFlag
    }
    const res = (await patientChatForArchives(params)) || {}
    const { titleType } = res
    /**
      title 类型
      HAVE_UN_PRIVATE(0, "已有非私聊"),
      HAVE_DONE_PRIVATE(2, "已有进行中私聊-非当前健管"),
      HAVE_DONE_PRIVATE_ON_DA(1, "已有进行中私聊-当前健管"),
      HAVE_PENDING_PRIVATE(3, "有待分配、待处理私聊"),
      HAVE_NO_WORK_ORDER(4, "不存在任何工单"),
      DA_ASSISTANT_NOT_ONLINE(5, "健管不在坐班中"),
      NO_GUIDE_CONSULT_GROUP(6,"没有导诊群"),
      CAN_NOT_CREATE_GUIDE(7,"不能创建导诊群")
     */
    switch (titleType) {
      case 0:
        Modal.confirm({
          title: () => '提示',
          icon: () => createVNode(ExclamationCircleOutlined),
          content: () => res.title,
          onOk() {
            handlePrivateChat(1)
          },
          onCancel() {}
        })
        break
      // 只是提示
      case 2:
      case 7:
        Modal.warn({
          title: () => '提示',
          content: res.title,
          onOk() {
            console.log('ok')
          }
        })
        break
      case 5:
        Modal.confirm({
          title: () => '提示',
          icon: () => createVNode(ExclamationCircleOutlined),
          content: () => res.title,
          okText: '继续沟通',
          onOk() {
            // 切换至坐班接收状态
            store.dispatch('user/handleChangeWorkStatus', { status: 0 }).then(async () => {
              // 0 - 坐班中(接收) 1 - 休息中 2 - 坐班中(拒收)
              await store.dispatch('user/queryShiftStaus')
              handlePrivateChat(0)
            })
          }
        })
        break
      case 6:
        Modal.confirm({
          title: () => '提示',
          content: res.title,
          onOk() {
            const params = {
              patientId: info.value.patientId,
              ifSendHelloMsg: 0
            }
            medicalGuide(params).then(_ => {
              handlePrivateChat(1)
            })
          }
        })
        break
      default:
        toPrivateGroup(res.groupId)
        break
    }
  } catch (error) {
    console.log(error)
  }
}

// 跳转到患者私聊群
const toPrivateGroup = groupId => {
  router.push({
    path: '/workplace'
  })
  store.commit('patientFile/SET_STATE', 2)
  store.commit('workOrder/SET_CHART_ID', String(groupId))
  changeToProcess()
}
const PreMedicalRecordModalRef = ref()
// 打开智能处方
const openMedicalCase = () => {
  // if (patientType.value === 0) return message.error('预注册患者不能打开预病例')
  PreMedicalRecordModalRef.value.modalInit({
    patientId: info.value.patientId,
    ucPatientId: info.value.ucPatientId,
    ucDoctorId: doctorInfo.value.ucDoctorId,
    doctorId: doctorInfo.value.doctorId
  })
}
const revisitConfirmRef = ref(null)
// 主动回访(1) / 分配回访(2)：单患者直接打开确认弹窗
const handleRevisit = visitAction => {
  const ucDoctorId = doctorInfo?.value?.ucDoctorId || 0
  const ucPatientId = info?.value?.ucPatientId || 0

  if (!ucDoctorId || !ucPatientId) return
  revisitConfirmRef.value?.initModal({
    visitAction,
    ucPatientIds: [ucPatientId],
    patientDoctorPairs: [{ ucPatientId: ucPatientId, ucDoctorId: ucDoctorId || 0 }]
  })
}
</script>

<style lang="scss" scoped>
.modal-footer {
  padding: 2px 0;
  text-align: right;
  .be-care-for {
    margin-right: 16px;
    display: inline-block;
    .text {
      font-weight: 400;
      font-size: 14px;
      color: #a10000;
    }
  }
}
.patient-cdr-tooltip {
  .ant-tooltip-inner {
    border-radius: 8px;
    padding: 12px;
    min-width: 350px;
    color: #fff;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }
}
</style>
